import dayjs from 'dayjs';
import { fetchPreciseOverview } from './customer.js';
import { callInternal } from './internal.js';
import { fetchStoreRanks, fetchTotalSummary, TYPE, type StoreRankItem, type TotalSummaryItem } from './statistic.js';

const EVENT_TYPES = [TYPE.sjzs, TYPE.dclsjs];

export interface ApacStoreDataMetric {
	value: number;
	trend: string;
	rankValue: number;
	average: number;
	exceed: string;
}

export interface ApacStoreDataOverview {
	date: string;
	previousDate: string;
	events: ApacStoreDataMetric;
	pendingEvents: ApacStoreDataMetric;
}

export type ApacStoreDataMetricKey =
	| 'entry_number'
	| 'total_number'
	| 'total_sales'
	| 'ecr'
	| 'events'
	| 'turnover_rate';

const STORE_DATA_TOTAL_TYPES: Partial<Record<ApacStoreDataMetricKey, string>> = {
	total_sales: TYPE.xsze,
	ecr: TYPE.jdzhl,
	events: TYPE.sjzs,
	turnover_rate: TYPE.cjl,
};

export async function fetchApacStoreEntryNumbers(
	storeIds: string[],
	date = dayjs().format('YYYY-MM-DD'),
): Promise<Record<string, number | null>> {
	return fetchApacStoreDataMetricValues({
		storeIds,
		metric: 'entry_number',
		startDate: date,
		endDate: date,
	});
}

export async function fetchApacStoreDataMetricValues(opts: {
	storeIds: string[];
	metric: ApacStoreDataMetricKey;
	startDate: string;
	endDate: string;
}): Promise<Record<string, number | null>> {
	const uniqueStoreIds = Array.from(new Set(opts.storeIds.map(String).filter(Boolean)));
	const pairs = await Promise.all(uniqueStoreIds.map(async (storeId) => {
		try {
			if (opts.metric === 'entry_number' || opts.metric === 'total_number') {
				const overview = await fetchPreciseOverview({
					storeIds: storeId,
					startDate: opts.startDate,
					endDate: opts.endDate,
				});
				return [storeId, numberOrNull(opts.metric === 'entry_number' ? overview?.all : overview?.pass_count)] as const;
			}
			const type = STORE_DATA_TOTAL_TYPES[opts.metric];
			if (!type) return [storeId, null] as const;
			const totals = await fetchTotalSummary({
				storeIds: storeId,
				startDate: opts.startDate,
				endDate: opts.endDate,
				types: [type],
			});
			const summary = totals.find((item) => String(item.type) === type);
			return [storeId, numberOrNull(summary?.value)] as const;
		} catch {
			return [storeId, null] as const;
		}
	}));
	const result: Record<string, number | null> = {};
	for (const [storeId, value] of pairs) {
		result[storeId] = value;
	}
	return result;
}

export async function fetchApacCollectedStoreIds(): Promise<string[]> {
	try {
		const res = await callInternal<unknown>({
			short_name: 'base',
			action: '/collect/all',
			av: '1',
		});
		if (Number(res.code) !== 0) return [];
		return Array.from(new Set(extractStoreIds(res.data)));
	} catch {
		return [];
	}
}

export async function setApacStoreCollected(storeId: string, collected: boolean): Promise<void> {
	const normalizedStoreId = String(storeId).trim();
	if (!normalizedStoreId) throw new Error('missing store id');
	const res = await callInternal<unknown>({
		short_name: 'base',
		action: collected ? '/collect/collect_store' : '/collect/uncollect_store',
		method: 'GET',
		query: { store_id: normalizedStoreId },
		av: '1',
	});
	if (Number(res.code) !== 0) {
		throw new Error(res.msg || 'store collection failed');
	}
}

export async function fetchApacStoreIdsByChannelStatus(status: 'online' | 'offline'): Promise<string[]> {
	const res = await callInternal<unknown>({
		short_name: 'base',
		action: '/FastDevice/filter_store_list',
		method: 'GET',
		query: { channel_status: status === 'online' ? 1 : 0 },
		av: '1',
	});
	if (Number(res.code) !== 0) {
		throw new Error(res.msg || 'store status filter failed');
	}
	return Array.from(new Set(extractStoreIds(res.data)));
}

export async function fetchApacStoreDataOverview(
	storeId: string,
	date = dayjs().format('YYYY-MM-DD'),
): Promise<ApacStoreDataOverview | null> {
	if (!storeId) return null;
	const previousDate = dayjs(date).subtract(1, 'day').format('YYYY-MM-DD');
	try {
		const [totals, ranks] = await Promise.all([
			fetchTotalSummary({
				storeIds: storeId,
				startDate: date,
				endDate: date,
				types: EVENT_TYPES,
			}).catch(() => [] as TotalSummaryItem[]),
			fetchStoreRanks({
				storeId,
				startDate: date,
				endDate: date,
				types: EVENT_TYPES,
			}).catch(() => ({} as Record<string, StoreRankItem>)),
		]);
		return {
			date,
			previousDate,
			events: buildMetric(totals, ranks[TYPE.sjzs], TYPE.sjzs),
			pendingEvents: buildMetric(totals, ranks[TYPE.dclsjs], TYPE.dclsjs),
		};
	} catch {
		return null;
	}
}

function buildMetric(
	totals: TotalSummaryItem[],
	rank: StoreRankItem | undefined,
	type: string,
): ApacStoreDataMetric {
	const summary = totals.find((item) => String(item.type) === type);
	const current = numberOrNull(summary?.value) ?? numberOrNull(rank?.value) ?? 0;
	const rankValue = numberOrNull(rank?.value) ?? current;
	const average = numberOrNull(rank?.b_avg) ?? 0;
	const exceed = numberOrNull(rank?.exceed) ?? 0;
	return {
		value: current,
		trend: formatTrend(current, numberOrNull(summary?.lastCycleValue)),
		rankValue,
		average,
		exceed: formatPercent(exceed),
	};
}

function extractStoreIds(value: unknown): string[] {
	if (Array.isArray(value)) {
		const ids: string[] = [];
		for (const item of value) ids.push(...extractStoreIds(item));
		return ids;
	}
	if (value == null) return [];
	if (typeof value === 'string' || typeof value === 'number') return [String(value)];
	if (typeof value !== 'object') return [];
	const obj = value as Record<string, unknown>;
	const direct = obj.store_id ?? obj.storeId ?? obj.group_id ?? obj.groupId;
	if (direct != null) return [String(direct)];
	const items = obj.stores ?? obj.items ?? obj.data ?? obj.list ?? obj.rows;
	return extractStoreIds(items);
}

function numberOrNull(value: unknown): number | null {
	if (value == null || value === '') return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}

function formatCount(value: number): string {
	if (Number.isInteger(value)) return String(value);
	return value.toFixed(2).replace(/\.?0+$/, '');
}

function formatTrend(current: number, previous: number | null): string {
	if (previous == null || previous === 0) return '0%';
	const change = ((current - previous) / Math.abs(previous)) * 100;
	return `${formatCount(change)}%`;
}

function formatPercent(value: number): string {
	const percent = Math.abs(value) <= 1 ? value * 100 : value;
	return `${formatCount(percent)}%`;
}
